import React, { useState, useMemo, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "./PortFolio.css";
import piechartIcon from "./assets/icons/pie-chart.png";

// Register necessary ChartJS components
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const IncomeChart = ({ userID, trading_accounts, accountMT5 }) => {
  const currentAccount = trading_accounts?.find(
    (account) => account.accountMT5 === accountMT5
  );

  if (!currentAccount) {
    return (
      <div className="incomechart-content-container">
        <p>No Trading Account found for account {accountMT5}</p>
      </div>
    );
  }

  const chartData = currentAccount.income_charts || [];
  const [sessionData, setSessionData] = useState(null); // Data from sessionStorage
  const [timeframe, setTimeframe] = useState("7-Days");

  // Fetch data from API and update sessionStorage
  const fetchYearData = async () => {
    const storedData = sessionStorage.getItem(`incomeData_${accountMT5}`);
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setSessionData(parsedData);
      return;
    }

    try {
      const response = await fetch(
        `https://admin.tducoin.com/api/tradingbot/incomecharts/${userID}/${accountMT5}`,
        {
          method: "GET",
          headers: {
            "x-api-key": "oqKbBxKcEn9l4IXE4EqS2sgNzXPFvE",
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      const newData = Array.isArray(data) ? data : [];
      // Merge with existing chartData and remove duplicates
      const mergedData = [
        ...new Map(
          [...chartData, ...newData].map((item) => [
            item.created_at,
            item,
          ])
        ).values(),
      ];
      setSessionData(mergedData);
      sessionStorage.setItem(
        `incomeData_${accountMT5}`,
        JSON.stringify(mergedData)
      );
    } catch (error) {
      console.error("Error fetching income data:", error);
      setSessionData(chartData); // Fallback to chartData if error occurs
    }
  };

  // Trigger API fetch when switching to "Year" and sessionData is not loaded
  useEffect(() => {
    if (timeframe === "Year" && sessionData === null) {
      fetchYearData();
    }
  }, [timeframe, sessionData]);

  // Parse data for all timeframes using sessionData or chartData
  const parsedData = useMemo(() => {
    const groupedData = {
      "7-Days": [],
      Month: [],
      Year: [],
      Decade: [],
    };

    const dataToParse = sessionData !== null ? sessionData : chartData;
    if (!Array.isArray(dataToParse) || dataToParse.length === 0) {
      return groupedData;
    }

    const now = new Date();
    const sortedData = [...dataToParse].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    // 7-Days: Last 7 days of data
    const last7Days = sortedData.slice(0, 7).reverse();
    last7Days.forEach(({ income_amount, created_at }) => {
      const date = new Date(created_at);
      const label = `${date.getDate()}/${date.getMonth() + 1}`;
      groupedData["7-Days"].push({ label, value: Number(income_amount) });
    });

    // Month: Data for the current month
    sortedData.forEach(({ income_amount, created_at }) => {
      const date = new Date(created_at);
      if (
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      ) {
        const label = `${date.getDate()}/${date.getMonth() + 1}`;
        groupedData.Month.push({ label, value: Number(income_amount) });
      }
    });
    groupedData.Month.reverse();

    // Year: Months up to the current month of the current year
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-based (e.g., April = 3)
    const yearMap = new Map();
    for (let month = 0; month <= currentMonth; month++) {
      const monthLabel = new Date(currentYear, month).toLocaleString(
        "default",
        { month: "short" }
      );
      yearMap.set(monthLabel, 0); // Initialize months up to current with 0
    }
    sortedData.forEach(({ income_amount, created_at }) => {
      const date = new Date(created_at);
      if (
        date.getFullYear() === currentYear &&
        date.getMonth() <= currentMonth
      ) {
        const monthLabel = date.toLocaleString("default", { month: "short" });
        yearMap.set(
          monthLabel,
          (yearMap.get(monthLabel) || 0) + Number(income_amount)
        );
      }
    });
    groupedData.Year = Array.from(yearMap.entries()).map(([label, value]) => ({
      label,
      value,
    }));

    // Decade: Aggregate data for all years within the last 10 years, newest on the right
    const decadeMap = new Map();
    sortedData.forEach(({ income_amount, created_at }) => {
      const date = new Date(created_at);
      const yearLabel = date.getFullYear();
      if (yearLabel >= now.getFullYear() - 9) {
        decadeMap.set(
          yearLabel,
          (decadeMap.get(yearLabel) || 0) + Number(income_amount)
        );
      }
    });
    groupedData.Decade = Array.from(decadeMap.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b[0] - a[0]) // Sort descending (newest to oldest), but chart will reverse it
      .reverse(); // Reverse to have newest on the right

    return groupedData;
  }, [chartData, sessionData, timeframe]);

  const timeframeData = parsedData[timeframe] || [];
  const labels = timeframeData.map((item) => item.label);
  const values = timeframeData.map((item) => item.value);

  return (
    <div className="incomechart-content-container">
      <div className="portfolio-header">
        <div className="portfolio-header-title">
          <img
            src={piechartIcon}
            alt="Pie Chart Icon"
            className="portfolio-icon"
          />
          <span>Income Chart</span>
        </div>
        <hr className="portfolio-divider-fullwidth" />
      </div>

      <div className="timeframe-selector">
        {["7-Days", "Month", "Year", "Decade"].map((tf) => (
          <label key={tf}>
            <input
              type="radio"
              value={tf}
              checked={timeframe === tf}
              onChange={(e) => setTimeframe(e.target.value)}
            />
            {tf}
          </label>
        ))}
      </div>

      <div className="chart-container">
        {timeframe === "Year" && sessionData === null ? (
          <p>Loading...</p>
        ) : labels.length > 0 ? (
          <Line
            data={{
              labels: labels,
              datasets: [
                {
                  data: values,
                  borderColor: "#4CAF50",
                  backgroundColor: "rgba(76, 175, 80, 0.2)",
                  borderWidth: 2,
                  tension: 0.4,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: true,
              aspectRatio: 3,
              plugins: {
                legend: { display: false },
                tooltip: { enabled: true },
              },
              scales: {
                x: { grid: { display: false } },
                y: { grid: { color: "#e0e0e0" } },
              },
            }}
          />
        ) : (
          <p>No data</p>
        )}
      </div>
    </div>
  );
};

export default IncomeChart;
